/**
 * `ColumnTypeUi` — the web half of the column-type registry (spec M1-B5 §6).
 *
 * `packages/shared/src/columns/<type>/` owns the value pipeline (parse, text, shadows, monday
 * interop, filter/summary SQL). This registry adds what only a browser needs — the icon, the
 * gallery entry, the default width and alignment, the renderer, the editor, the settings
 * dialog, the filter editor, the footer, and the clipboard pair — keyed by the same `type`
 * string. Adding a type is one folder here plus one `registerColumnUi()` line; nothing outside
 * a type's folder branches on the type (CLAUDE.md rule 6).
 *
 * Two members are **ours, not in B5 §6**, and exist because the table view runs against an
 * in-memory source in this round:
 *
 *   `bucketsOf`  — the buckets a cell belongs to. One member serves three surfaces that all
 *                  need "which values does this cell match": `any_of` filtering (status by
 *                  label id, dropdown by option id, people by person id — never by text
 *                  `[cvs§4.7]`), the quick-filter facets `[B4§5.4]`, and group-by `[B4§8.8]`.
 *                  On the server the same three are SQL over the shadows; the shapes agree.
 *   `summarise`  — the footer value for the aggregations only the type can compute (the status
 *                  battery, the people avatars). Everything numeric/date/count is generic and
 *                  lives in `../table/summaries.ts`.
 *
 * B4 §8.8 asks for `groupByKey` in `packages/shared`; that package is owned by another task
 * this round, so it lives here as `bucketsOf` and moves down when the tRPC source lands
 * (recorded in `tasks/T-B4.md`).
 */
import type { ColumnAggregation } from '@elizmonday/shared';
import type { IconName } from '@elizmonday/ui';
import type { ComponentType } from 'react';
import type { BoardColumn, ItemRow, Person, SummaryValue } from '../data/types';

export type ColumnAlign = 'left' | 'center' | 'right';

export type GalleryGroup = 'essentials' | 'people' | 'dates' | 'numbers' | 'advanced';

/** One bucket a cell belongs to: the id `any_of` compares, plus how to render it. */
export interface CellBucket {
  /** Stable, type-owned key: a status label id, a dropdown option id, a person id, a date. */
  readonly key: string | number;
  readonly label: string;
  /** Hex, when the type has a colour for the bucket (status, dropdown, checkbox). */
  readonly color?: string;
}

/** One account tag, as the `tags` column's renderer and picker need it. */
export interface TagRef {
  readonly id: number;
  readonly name: string;
  readonly color?: string | null;
}

export interface CellRenderContext {
  readonly people: readonly Person[];
  /**
   * The account tag vocabulary (`tags` is account-level in monday, not per board
   * `[09§A.7 tags]`). Optional: a caller that has not loaded it gets id chips rather than
   * names, and never a wrong name.
   */
  readonly tags?: readonly TagRef[];
  /**
   * `create_or_get_tag` (q340, P-122): minting an account tag is a **board-structure** call, not
   * a cell write, so the picker cannot do it itself — the board screen supplies it here and the
   * type folder simply asks. Absent (a unit test, a read-only surface) = the `Create "…"` row
   * stays disabled, which is what it always was.
   */
  createTag?(name: string): Promise<TagRef>;
  /**
   * The board's other columns, for the two types whose cell is computed from **sibling**
   * columns: `progress` (the done state of the status columns its settings name) and
   * `formula` (a `{ref}` resolves to another column). Optional, so a unit test can render a
   * cell with nothing but its own value.
   */
  readonly columns?: readonly BoardColumn[];
  /** Board search term; renderers wrap matches in `<mark>` `[B4§8.9]`. */
  readonly search: string;
  readonly timezone: string;
}

export interface CellRendererProps<TValue = unknown, TSettings = unknown> {
  readonly value: TValue | null;
  readonly settings: TSettings;
  readonly column: BoardColumn;
  readonly item: ItemRow;
  readonly context: CellRenderContext;
}

export interface CellEditorProps<TValue = unknown, TSettings = unknown> {
  readonly value: TValue | null;
  readonly settings: TSettings;
  readonly column: BoardColumn;
  readonly item: ItemRow;
  readonly context: CellRenderContext;
  readonly readOnly: boolean;
  readonly autoFocus: boolean;
  /** Set when the editor was opened by typing a printable key `[B5§6.1]`. */
  readonly initialInput?: string;
  onCommit(next: TValue | null): void;
  onCancel(): void;
  onNavigate(direction: 'up' | 'down' | 'left' | 'right' | 'next' | 'prev'): void;
}

export interface ColumnSettingsProps<TSettings = unknown> {
  readonly column: BoardColumn;
  readonly settings: TSettings;
  readonly revision: string;
  /**
   * The board's **other** columns, for the two types whose settings name sibling columns:
   * `progress` (the status columns it tracks and their weights, `related_columns.columns`) and
   * `mirror` (the `board_relation` column the reflection follows, `relation_column`). It is the
   * settings-side twin of `CellRenderContext.columns` and is optional for the same reason — a
   * unit test renders a dialog with nothing but its own settings (card P-123).
   */
  readonly columns?: readonly BoardColumn[];
  onChange(next: TSettings): void;
  onCommit(next: TSettings): void;
  onCancel(): void;
}

export interface FilterEditorProps<TSettings = unknown> {
  readonly settings: TSettings;
  readonly column: BoardColumn;
  readonly value: unknown;
  readonly buckets: readonly CellBucket[];
  onChange(next: unknown): void;
}

export interface SummaryProps {
  readonly summary: SummaryValue;
  readonly column: BoardColumn;
}

export interface ColumnTypeUi<TValue = unknown, TSettings = unknown> {
  readonly type: string;
  readonly icon: IconName;
  readonly gallery: { readonly group: GalleryGroup; readonly keywords: readonly string[] };
  /**
   * §8.1, parity card P-022: **140 px** for every type whose stored width is `null`, except
   * `people` (98) and `creation_log` / `last_updated` (135), all three measured. `name`'s is
   * the whole sticky region, not a cell width (P-021).
   */
  readonly defaultWidth: number;
  /**
   * `false` when the type is **not** a scrolling grid column (parity card P-023).
   *
   * `subtasks` is the only one: monday renders no `Subitems` header and no column for it —
   * the ⊕ expander lives in the sticky region's 65 px conversation cell. The column is still a
   * real column everywhere else (`boards.columns`, *Hide*, the column menu, the API), so this
   * is a **layout** opt-out, read by `table/derive.ts`, not a hidden column.
   */
  readonly gridColumn?: boolean;
  /** status/people/date centred, text/name left, numbers right `[B5§6]`, `[M2§6]`. */
  readonly align: ColumnAlign;
  readonly Cell: ComponentType<CellRendererProps<TValue, TSettings>>;
  /** Absent = a read-only type; the grid never opens an editor for it `[B5§9 E5]`. */
  readonly Editor?: ComponentType<CellEditorProps<TValue, TSettings>>;
  readonly SettingsDialog?: ComponentType<ColumnSettingsProps<TSettings>>;
  readonly FilterEditor?: ComponentType<FilterEditorProps<TSettings>>;
  readonly Summary?: ComponentType<SummaryProps>;
  /**
   * Inverse of `toText`. Throws when the text does not resolve — the grid catches, leaves the
   * cell unchanged and counts it in the paste summary `[B4§8.4]`. Takes the render context
   * because `people` and `person` have to turn display names back into ids.
   */
  fromClipboard(text: string, settings: TSettings, context: CellRenderContext): TValue | null;
  /**
   * === `toText` `[B5§6.1]`, so a copied column pastes into Excel as monday renders it.
   *
   * **P-126.** It takes the render context for the same reason `fromClipboard` does: the
   * people-like types hold ids, and the *names* — which is what monday's grid copies and what
   * our own `fromClipboard` reads back — live in the context. The API `text` and the clipboard
   * text are two contracts: `person`'s `/v2` `text` is empty on purpose `[cvs§2 person]`, its
   * clipboard text is the assignee's name.
   */
  toClipboard(value: TValue | null, settings: TSettings, context: CellRenderContext): string;
  /** See the module header. `[]` = the cell is empty and belongs to the `blank` bucket. */
  bucketsOf(value: TValue | null, settings: TSettings, context: CellRenderContext): CellBucket[];
  /** Only for aggregations the generic implementation cannot do (battery, avatars). */
  summarise?(
    aggregation: ColumnAggregation,
    cells: readonly (TValue | null)[],
    settings: TSettings,
    context: CellRenderContext,
  ): SummaryValue | null;
  /**
   * The **cover image** this cell offers a card, as a URL, or `null` when it offers none
   * (`specs/M4-V2-kanban.md` §7.5, `specs/M4-V7-gallery.md` §7.3).
   *
   * A kanban or Cards card draws a 150 px band above its header from whichever column the
   * view's *Cover* setting names, and only the type knows what a picture is in its own value:
   * `file` answers with its first image asset, `people` / `person` with the photo of the
   * first person, and every other type omits the member and offers nothing. The views used to
   * probe the value's *shape* from `views/shared/cover.ts` because this tree belonged to
   * another worker (recorded on `tasks/T-M4-V2-V4-V7.md`); this is where it belongs
   * (CLAUDE.md rule 6).
   *
   * The view owns the *fit* (`crop` / `contain`), not the type — it is a view setting.
   */
  coverImageOf?(value: TValue | null, settings: TSettings, context: CellRenderContext): string | null;
  /** `true` when the type may be a group-by key `[B4§8.8]`. */
  readonly groupable: boolean;
  /**
   * `true` when a cell of this type holds a value that means the **same thing on every item**
   * — a status label, a date, a number, a tag — so the one value a user picked can be written
   * to a whole row selection at once (parity card P-263).
   *
   * monday applies an editor commit made on a **ticked** row to every ticked row of that
   * column (`batch_change_pulses_column_value`), and the grid asks the type instead of
   * branching on it (CLAUDE.md rule 6). Omitted = `false`, the conservative reading: the edit
   * stays on its own row. It is deliberately **not** derived from `writable`, because the
   * types that are writable yet item-*dependent* would then be swept in — `name` (every item
   * has its own), `long_text` (a note about one item), `file` (that item's assets),
   * `time_tracking` (that item's sessions), `board_relation` / `dependency` (links from that
   * item), `location`, `person`. Those keep the single-row path until a capture says otherwise
   * (`docs/open-questions.md`).
   */
  readonly massEditable?: boolean;
  /**
   * Opt out of "first click selects, second click edits".
   *
   * Every editor monday opens needs two clicks — measured, per type, in
   * `discovery/ui-reference/editors/open-rule.json` and each type's `inline-behaviour.json`
   * (`openedBy: "click2"`). `checkbox` is the exception because it has no editor at all: one
   * click flips the value in place, which the capture shows directly (`cell-normal.png` empty
   * → one click → `cell-toggled.png` with the green tick). The grid asks the type instead of
   * branching on `type === 'checkbox'` (CLAUDE.md rule 6).
   */
  readonly activateOnFirstClick?: boolean;
  /**
   * Open the editor even when the cell is **read-only**.
   *
   * Three tier-2 types have a panel rather than a value editor — `file`'s gallery,
   * `time_tracking`'s timer and log, `mirror`'s reflected list — and monday opens all three on
   * a column whose *value* cannot be written (`[09§A.7]`: files are added with
   * `add_file_to_column`, the timer is started and stopped, a mirror reflects). Without this
   * the grid would refuse to show them at all, because a `writable: false` type is marked
   * read-only board-wide (M2 tier-2 §6).
   *
   * It does **not** loosen anything: the editor still receives `readOnly` and the api still
   * refuses a value write for the type.
   */
  readonly opensWhenReadOnly?: boolean;
}

/** Keeps the generics inferred while enforcing the contract, exactly like `defineColumnType`. */
export function defineColumnUi<TValue, TSettings>(
  definition: ColumnTypeUi<TValue, TSettings>,
): ColumnTypeUi<TValue, TSettings> {
  return definition;
}

// Generic erasure: the registry holds definitions with different value/settings types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyColumnTypeUi = ColumnTypeUi<any, any>;
